import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Label,
    Badge,
    Spinner,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const InventoryManagement = () => {

    document.title = "HMS | Inventory Management";

    /* =========================================================
       FILTERS
    ========================================================= */

    const [filters, setFilters] = useState({
        roomType: "",
        startDate: "",
        endDate: "",
    });

    /* =========================================================
       MASTER DATA
    ========================================================= */

    const [roomTypes, setRoomTypes] = useState([]);

    const [inventoryData, setInventoryData] = useState([]);

    const [inventoryLogs, setInventoryLogs] = useState([]);

    const [loading, setLoading] = useState(false);

    const [updating, setUpdating] = useState(false);

    /* =========================================================
       BULK UPDATE
    ========================================================= */

    const [bulkUpdate, setBulkUpdate] = useState({
        roomTypeId: "",
        startDate: "",
        endDate: "",
        totalRooms: "",
    });

    /* =========================================================
       WIDGETS
    ========================================================= */

    const [widgets, setWidgets] = useState([
        {
            label: "Total Rooms",
            value: 0,
            icon: "bx bx-building-house",
            color: "#0d6efd",
            bgColor: "rgba(13,110,253,.12)",
        },
        {
            label: "Available",
            value: 0,
            icon: "bx bx-check-circle",
            color: "#198754",
            bgColor: "rgba(25,135,84,.12)",
        },
        {
            label: "Booked",
            value: 0,
            icon: "bx bx-calendar-check",
            color: "#ffc107",
            bgColor: "rgba(255,193,7,.15)",
        },
        {
            label: "Blocked",
            value: 0,
            icon: "bx bx-block",
            color: "#dc3545",
            bgColor: "rgba(220,53,69,.12)",
        },
    ]);

    /* =========================================================
       GENERATE DATE RANGE
    ========================================================= */

    const dateRange = useMemo(() => {

        if (
            !filters.startDate ||
            !filters.endDate
        ) {
            return [];
        }

        const dates = [];

        const start = new Date(
            `${filters.startDate}T00:00:00`
        );

        const end = new Date(
            `${filters.endDate}T00:00:00`
        );

        const current = new Date(start);

        while (current <= end) {

            dates.push(
                new Date(current)
            );

            current.setDate(
                current.getDate() + 1
            );
        }

        return dates;

    }, [
        filters.startDate,
        filters.endDate,
    ]);

    /* =========================================================
       FORMAT DATE
    ========================================================= */

    const formatDate = (date) => {

        return date.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
            }
        );
    };

    const formatDay = (date) => {

        return date.toLocaleDateString(
            "en-US",
            {
                weekday: "short",
            }
        );
    };

    /* =========================================================
       INVENTORY TABLE COLUMNS
    ========================================================= */

    const inventoryColumns = useMemo(() => {

        const columns = [
            {
                header: "Room Type",
                accessorKey: "roomType",
                cell: ({
                    getValue,
                }) => (
                    <span className="fw-semibold">
                        {getValue()}
                    </span>
                ),
            },
        ];

        dateRange.forEach(
            (date, index) => {

                columns.push({
                    header: (
                        <div className="text-center">
                            <div className="fw-semibold">
                                {formatDay(date)}
                            </div>

                            <small className="text-muted">
                                {formatDate(date)}
                            </small>
                        </div>
                    ),

                    accessorKey:
                        `d${index + 1}`,

                    cell: ({
                        getValue,
                    }) => {

                        const value =
                            getValue();

                        let badgeColor =
                            "success";

                        if (
                            Number(value) === 0
                        ) {
                            badgeColor =
                                "danger";
                        } else if (
                            Number(value) <= 2
                        ) {
                            badgeColor =
                                "warning";
                        }

                        return (
                            <div className="text-center">
                                <Badge
                                    color={
                                        badgeColor
                                    }
                                    pill
                                >
                                    {
                                        value ??
                                        0
                                    }
                                </Badge>
                            </div>
                        );
                    },
                });
            }
        );

        return columns;

    }, [dateRange]);

    /* =========================================================
       LOG COLUMNS
    ========================================================= */

    const logColumns = useMemo(
        () => [
            {
                header: "Date",
                accessorKey: "date",
            },
            {
                header: "Room Type",
                accessorKey: "roomType",
            },
            {
                header: "Old",
                accessorKey:
                    "oldInventory",
            },
            {
                header: "New",
                accessorKey:
                    "newInventory",
            },
            {
                header: "Updated By",
                accessorKey:
                    "updatedBy",
            },
        ],
        []
    );

    /* =========================================================
       LOAD ROOM TYPES
    ========================================================= */

    const loadRoomTypes = async () => {

        try {

            const res =
                await api.get(
                    "/manage/room-types"
                );

            setRoomTypes(
                res.data || []
            );

        } catch (err) {

            console.error(
                "ROOM TYPES ERROR",
                err
            );
        }
    };

    /* =========================================================
       LOAD LOGS
    ========================================================= */

    const loadLogs = async () => {

        try {

            const res =
                await api.get(
                    "/inventory/logs"
                );

            const rows =
                (
                    res.data.logs ||
                    []
                ).map(
                    (item) => ({
                        date:
                            item.date,

                        roomType:
                            item.roomTypeName,

                        oldInventory:
                            item.oldInventory,

                        newInventory:
                            item.newInventory,

                        updatedBy:
                            item.updatedBy,
                    })
                );

            setInventoryLogs(
                rows
            );

        } catch (err) {

            console.error(
                "LOG ERROR",
                err
            );
        }
    };

    /* =========================================================
       LOAD CALENDAR
    ========================================================= */

    const loadCalendar = async () => {

        if (
            !filters.startDate ||
            !filters.endDate
        ) {
            return;
        }

        try {

            setLoading(true);

            const res =
                await api.get(
                    "/inventory/calendar",
                    {
                        params: {
                            startDate:
                                filters.startDate,

                            endDate:
                                filters.endDate,

                            roomTypeId:
                                filters.roomType,
                        },
                    }
                );

            const calendar =
                res.data?.data ||
                [];

            let totalInventory = 0;
            let availableRooms = 0;
            let bookedRooms = 0;
            let blockedRooms = 0;

            const rows = [];

            calendar.forEach(
                (room) => {

                    const row = {
                        roomType:
                            room.roomTypeName,
                    };

                    (
                        room.inventory ||
                        []
                    ).forEach(
                        (
                            day,
                            index
                        ) => {

                            row[
                                `d${index + 1}`
                            ] =
                                Number(
                                    day.availableRooms ||
                                    0
                                );

                            totalInventory +=
                                Number(
                                    day.totalRooms ||
                                    0
                                );

                            availableRooms +=
                                Number(
                                    day.availableRooms ||
                                    0
                                );

                            bookedRooms +=
                                Number(
                                    day.bookedRooms ||
                                    0
                                );

                            blockedRooms +=
                                Number(
                                    day.blockedRooms ||
                                    0
                                );
                        }
                    );

                    rows.push(row);
                }
            );

            setInventoryData(
                rows
            );

            setWidgets([
                {
                    label:
                        "Total Rooms",
                    value:
                        totalInventory,
                    icon:
                        "bx bx-building-house",
                    color:
                        "#0d6efd",
                    bgColor:
                        "rgba(13,110,253,.12)",
                },
                {
                    label:
                        "Available",
                    value:
                        availableRooms,
                    icon:
                        "bx bx-check-circle",
                    color:
                        "#198754",
                    bgColor:
                        "rgba(25,135,84,.12)",
                },
                {
                    label:
                        "Booked",
                    value:
                        bookedRooms,
                    icon:
                        "bx bx-calendar-check",
                    color:
                        "#ffc107",
                    bgColor:
                        "rgba(255,193,7,.15)",
                },
                {
                    label:
                        "Blocked",
                    value:
                        blockedRooms,
                    icon:
                        "bx bx-block",
                    color:
                        "#dc3545",
                    bgColor:
                        "rgba(220,53,69,.12)",
                },
            ]);

        } catch (err) {

            console.error(
                "CALENDAR ERROR",
                err
            );

        } finally {

            setLoading(false);
        }
    };

    /* =========================================================
       UPDATE INVENTORY
    ========================================================= */

    const handleUpdateInventory =
        async () => {

            if (
                !bulkUpdate.roomTypeId
            ) {

                alert(
                    "Please select a room type"
                );

                return;
            }

            if (
                !bulkUpdate.startDate ||
                !bulkUpdate.endDate
            ) {

                alert(
                    "Please select start and end date"
                );

                return;
            }

            if (
                bulkUpdate.totalRooms ===
                ""
            ) {

                alert(
                    "Please enter inventory"
                );

                return;
            }

            if (
                Number(
                    bulkUpdate.totalRooms
                ) < 0
            ) {

                alert(
                    "Inventory cannot be negative"
                );

                return;
            }

            try {

                setUpdating(true);

                await api.post(
                    "/inventory/update",
                    {
                        roomTypeId:
                            bulkUpdate.roomTypeId,

                        startDate:
                            bulkUpdate.startDate,

                        endDate:
                            bulkUpdate.endDate,

                        totalRooms:
                            Number(
                                bulkUpdate.totalRooms
                            ),
                    }
                );

                alert(
                    "Inventory updated successfully"
                );

                await loadCalendar();

                await loadLogs();

            } catch (err) {

                console.error(
                    "INVENTORY UPDATE ERROR",
                    err
                );

                alert(
                    err
                        ?.response
                        ?.data
                        ?.message ||
                    "Update failed"
                );

            } finally {

                setUpdating(false);
            }
        };

    /* =========================================================
       INITIAL LOAD
    ========================================================= */

    useEffect(() => {

        const today =
            new Date();

        const start =
            today
                .toISOString()
                .split("T")[0];

        const end =
            new Date();

        end.setDate(
            end.getDate() + 6
        );

        const endDate =
            end
                .toISOString()
                .split("T")[0];

        setFilters({
            roomType: "",
            startDate: start,
            endDate: endDate,
        });

        setBulkUpdate({
            roomTypeId: "",
            startDate: start,
            endDate: endDate,
            totalRooms: "",
        });

        loadRoomTypes();

    }, []);

    /* =========================================================
       INITIAL CALENDAR LOAD
    ========================================================= */

    useEffect(() => {

        if (
            filters.startDate &&
            filters.endDate
        ) {

            loadCalendar();

            loadLogs();
        }

    }, [
        filters.startDate,
        filters.endDate,
        filters.roomType,
    ]);

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <div className="page-content">

            <Container fluid>

                <BreadCrumb
                    title="Channel Manager"
                    pageTitle="Inventory Management"
                />

                {/* =====================================================
                    SUMMARY WIDGETS
                ===================================================== */}

                <Row>

                    {widgets.map(
                        (
                            item,
                            index
                        ) => (

                            <Col
                                xl={3}
                                md={6}
                                key={index}
                            >

                                <Card>

                                    <CardBody
                                        className="d-flex justify-content-between align-items-center"
                                    >

                                        <div>

                                            <p className="text-uppercase text-muted fw-semibold mb-1">

                                                {
                                                    item.label
                                                }

                                            </p>

                                            <h3 className="mb-0">

                                                <CountUp
                                                    start={0}
                                                    end={
                                                        item.value
                                                    }
                                                    duration={
                                                        1.2
                                                    }
                                                />

                                            </h3>

                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{
                                                width:
                                                    "50px",

                                                height:
                                                    "50px",

                                                borderRadius:
                                                    "10px",

                                                backgroundColor:
                                                    item.bgColor,
                                            }}
                                        >

                                            <i
                                                className={
                                                    item.icon
                                                }
                                                style={{
                                                    fontSize:
                                                        "24px",

                                                    color:
                                                        item.color,
                                                }}
                                            />

                                        </div>

                                    </CardBody>

                                </Card>

                            </Col>
                        )
                    )}

                </Row>

                {/* =====================================================
                    FILTERS
                ===================================================== */}

                <Card>

                    <CardHeader>

                        <div className="d-flex justify-content-between align-items-center">

                            <div>

                                <h5 className="mb-1">
                                    Inventory Filters
                                </h5>

                                <p className="text-muted mb-0">
                                    Select the inventory period and room type
                                </p>

                            </div>

                            <Badge
                                color="primary"
                                className="px-3 py-2"
                            >
                                {dateRange.length} Days
                            </Badge>

                        </div>

                    </CardHeader>

                    <CardBody>

                        <Row>

                            <Col md={3}>

                                <Label>
                                    Start Date
                                </Label>

                                <Input
                                    type="date"
                                    value={
                                        filters.startDate
                                    }
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            startDate:
                                                e.target.value,
                                        })
                                    }
                                />

                            </Col>

                            <Col md={3}>

                                <Label>
                                    End Date
                                </Label>

                                <Input
                                    type="date"
                                    value={
                                        filters.endDate
                                    }
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            endDate:
                                                e.target.value,
                                        })
                                    }
                                />

                            </Col>

                            <Col md={3}>

                                <Label>
                                    Room Type
                                </Label>

                                <Input
                                    type="select"
                                    value={
                                        filters.roomType
                                    }
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            roomType:
                                                e.target.value,
                                        })
                                    }
                                >

                                    <option value="">
                                        All Room Types
                                    </option>

                                    {roomTypes.map(
                                        (
                                            room
                                        ) => (

                                            <option
                                                key={
                                                    room._id
                                                }
                                                value={
                                                    room._id
                                                }
                                            >
                                                {
                                                    room.name
                                                }
                                            </option>

                                        )
                                    )}

                                </Input>

                            </Col>

                            <Col
                                md={3}
                                className="d-flex align-items-end"
                            >

                                <Button
                                    color="primary"
                                    className="w-100"
                                    onClick={
                                        loadCalendar
                                    }
                                    disabled={
                                        loading
                                    }
                                >

                                    {loading ? (
                                        <>
                                            <Spinner
                                                size="sm"
                                                className="me-2"
                                            />

                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-filter-alt me-1" />

                                            Apply Filters
                                        </>
                                    )}

                                </Button>

                            </Col>

                        </Row>

                    </CardBody>

                </Card>

                {/* =====================================================
                    INVENTORY CALENDAR
                ===================================================== */}

                <Card>

                    <CardHeader>

                        <div className="d-flex justify-content-between align-items-center">

                            <div>

                                <h5 className="mb-1">
                                    Inventory Calendar
                                </h5>

                                <p className="text-muted mb-0">
                                    Master inventory available for room sales
                                </p>

                            </div>

                            <div className="d-flex align-items-center gap-2">

                                <Badge color="success">
                                    <i className="bx bx-radio-circle me-1" />
                                    Master Inventory
                                </Badge>

                                {loading && (
                                    <Spinner
                                        size="sm"
                                        color="primary"
                                    />
                                )}

                            </div>

                        </div>

                    </CardHeader>

                    <CardBody>

                        <div
                            className="table-responsive"
                            style={{
                                overflowX:
                                    "auto",
                            }}
                        >

                            <TableContainer
                                columns={
                                    inventoryColumns
                                }
                                data={
                                    inventoryData
                                }
                                customPageSize={
                                    10
                                }
                                isPagination={
                                    false
                                }
                                className="text-nowrap"
                                theadClass="table-light"
                            />

                        </div>

                    </CardBody>

                </Card>

                {/* =====================================================
                    CHANNEL SYNC INFORMATION
                ===================================================== */}

                <Card>

                    <CardHeader>

                        <div className="d-flex justify-content-between align-items-center">

                            <div>

                                <h5 className="mb-1">
                                    Channel Distribution
                                </h5>

                                <p className="text-muted mb-0">
                                    Master inventory will be distributed to connected OTAs
                                </p>

                            </div>

                            <Badge color="secondary">
                                Channel Manager
                            </Badge>

                        </div>

                    </CardHeader>

                    <CardBody>

                        <Row>

                            <Col
                                xl={3}
                                md={6}
                            >

                                <div className="border rounded p-3 mb-3 mb-xl-0">

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div className="d-flex align-items-center">

                                            <div
                                                className="avatar-sm me-3"
                                            >

                                                <div className="avatar-title bg-light rounded-circle">

                                                    <i className="bx bx-hotel text-primary fs-4" />

                                                </div>

                                            </div>

                                            <div>

                                                <h6 className="mb-1">
                                                    Booking.com
                                                </h6>

                                                <span className="text-muted fs-12">
                                                    Channel
                                                </span>

                                            </div>

                                        </div>

                                        <Badge color="warning">
                                            Not Configured
                                        </Badge>

                                    </div>

                                </div>

                            </Col>

                            <Col
                                xl={3}
                                md={6}
                            >

                                <div className="border rounded p-3 mb-3 mb-xl-0">

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div className="d-flex align-items-center">

                                            <div className="avatar-sm me-3">

                                                <div className="avatar-title bg-light rounded-circle">

                                                    <i className="bx bx-globe text-info fs-4" />

                                                </div>

                                            </div>

                                            <div>

                                                <h6 className="mb-1">
                                                    Agoda
                                                </h6>

                                                <span className="text-muted fs-12">
                                                    Channel
                                                </span>

                                            </div>

                                        </div>

                                        <Badge color="secondary">
                                            Not Configured
                                        </Badge>

                                    </div>

                                </div>

                            </Col>

                            <Col
                                xl={3}
                                md={6}
                            >

                                <div className="border rounded p-3 mb-3 mb-xl-0">

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div className="d-flex align-items-center">

                                            <div className="avatar-sm me-3">

                                                <div className="avatar-title bg-light rounded-circle">

                                                    <i className="bx bx-globe text-warning fs-4" />

                                                </div>

                                            </div>

                                            <div>

                                                <h6 className="mb-1">
                                                    Expedia
                                                </h6>

                                                <span className="text-muted fs-12">
                                                    Channel
                                                </span>

                                            </div>

                                        </div>

                                        <Badge color="secondary">
                                            Not Configured
                                        </Badge>

                                    </div>

                                </div>

                            </Col>

                            <Col
                                xl={3}
                                md={6}
                            >

                                <div className="border rounded p-3">

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div className="d-flex align-items-center">

                                            <div className="avatar-sm me-3">

                                                <div className="avatar-title bg-light rounded-circle">

                                                    <i className="bx bx-globe text-success fs-4" />

                                                </div>

                                            </div>

                                            <div>

                                                <h6 className="mb-1">
                                                    Other OTA
                                                </h6>

                                                <span className="text-muted fs-12">
                                                    Channel
                                                </span>

                                            </div>

                                        </div>

                                        <Badge color="secondary">
                                            Not Configured
                                        </Badge>

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </CardBody>

                </Card>

                {/* =====================================================
                    BULK UPDATE + LOGS
                ===================================================== */}

                <Row>

                    <Col lg={4}>

                        <Card>

                            <CardHeader>

                                <h5 className="mb-1">
                                    Bulk Inventory Update
                                </h5>

                                <p className="text-muted mb-0">
                                    Update master room inventory for a date range
                                </p>

                            </CardHeader>

                            <CardBody>

                                <div className="mb-3">

                                    <Label>
                                        Room Type
                                    </Label>

                                    <Input
                                        type="select"
                                        value={
                                            bulkUpdate.roomTypeId
                                        }
                                        onChange={(e) =>
                                            setBulkUpdate({
                                                ...bulkUpdate,
                                                roomTypeId:
                                                    e.target.value,
                                            })
                                        }
                                    >

                                        <option value="">
                                            Select Room Type
                                        </option>

                                        {roomTypes.map(
                                            (
                                                room
                                            ) => (

                                                <option
                                                    key={
                                                        room._id
                                                    }
                                                    value={
                                                        room._id
                                                    }
                                                >
                                                    {
                                                        room.name
                                                    }
                                                </option>

                                            )
                                        )}

                                    </Input>

                                </div>

                                <div className="mb-3">

                                    <Label>
                                        Start Date
                                    </Label>

                                    <Input
                                        type="date"
                                        value={
                                            bulkUpdate.startDate
                                        }
                                        onChange={(e) =>
                                            setBulkUpdate({
                                                ...bulkUpdate,
                                                startDate:
                                                    e.target.value,
                                            })
                                        }
                                    />

                                </div>

                                <div className="mb-3">

                                    <Label>
                                        End Date
                                    </Label>

                                    <Input
                                        type="date"
                                        value={
                                            bulkUpdate.endDate
                                        }
                                        onChange={(e) =>
                                            setBulkUpdate({
                                                ...bulkUpdate,
                                                endDate:
                                                    e.target.value,
                                            })
                                        }
                                    />

                                </div>

                                <div className="mb-4">

                                    <Label>
                                        Total Sellable Rooms
                                    </Label>

                                    <Input
                                        type="number"
                                        min="0"
                                        value={
                                            bulkUpdate.totalRooms
                                        }
                                        onChange={(e) =>
                                            setBulkUpdate({
                                                ...bulkUpdate,
                                                totalRooms:
                                                    e.target.value,
                                            })
                                        }
                                    />

                                    <small className="text-muted">
                                        This updates the master inventory. Connected channels will use this inventory when synchronization is enabled.
                                    </small>

                                </div>

                                <Button
                                    color="success"
                                    className="w-100"
                                    onClick={
                                        handleUpdateInventory
                                    }
                                    disabled={
                                        updating
                                    }
                                >

                                    {updating ? (
                                        <>
                                            <Spinner
                                                size="sm"
                                                className="me-2"
                                            />

                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-save me-1" />

                                            Update Inventory
                                        </>
                                    )}

                                </Button>

                            </CardBody>

                        </Card>

                    </Col>

                    <Col lg={8}>

                        <Card>

                            <CardHeader>

                                <h5 className="mb-1">
                                    Recent Inventory Updates
                                </h5>

                                <p className="text-muted mb-0">
                                    Audit history of inventory changes
                                </p>

                            </CardHeader>

                            <CardBody>

                                <TableContainer
                                    columns={
                                        logColumns
                                    }
                                    data={
                                        inventoryLogs
                                    }
                                    customPageSize={
                                        5
                                    }
                                    isPagination={
                                        false
                                    }
                                    className="text-nowrap"
                                    theadClass="table-light"
                                />

                            </CardBody>

                        </Card>

                    </Col>

                </Row>

            </Container>

        </div>
    );
};

export default InventoryManagement;